import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';

interface BillingInfo {
  planName: string;
  planStatus: 'active' | 'trialing' | 'past_due' | 'cancelled';
  trialDaysRemaining: number | null;
  hasStripeSubscription: boolean;
}

export function TrialBanner(): JSX.Element | null {
  const navigate = useNavigate();
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await api.get<BillingInfo>('/billing/info');
        if (!cancelled && res.success && res.data) {
          setBilling(res.data);
        }
      } catch {
        // silently fail — don't block the app
      }
    }
    void load();
    return () => { cancelled = true; };
  }, []);

  if (dismissed || !billing) return null;
  if (billing.planStatus === 'active') return null;

  // Determine banner config
  let bgClass = '';
  let message = '';
  let ctaLabel = '';
  let ctaAction: () => void = () => {};

  if (billing.planStatus === 'trialing' && billing.trialDaysRemaining != null && billing.trialDaysRemaining <= 4) {
    bgClass = 'bg-amber-500';
    message = `Your trial ends in ${billing.trialDaysRemaining} day${billing.trialDaysRemaining === 1 ? '' : 's'}. Subscribe to keep using MSK Insight.`;
    ctaLabel = 'Subscribe Now';
    ctaAction = () => navigate('/pricing');
  } else if (billing.planStatus === 'cancelled') {
    bgClass = 'bg-red-600';
    message = 'Your trial has ended. Subscribe to continue using MSK Insight.';
    ctaLabel = 'View Plans';
    ctaAction = () => navigate('/pricing');
  } else if (billing.planStatus === 'past_due') {
    bgClass = 'bg-orange-500';
    message = 'Payment failed. Please update your payment method.';
    ctaLabel = 'Update Payment';
    ctaAction = async () => {
      try {
        const res = await api.get<{ url: string }>('/billing/portal');
        if (res.success && res.data?.url) {
          window.open(res.data.url, '_blank');
        }
      } catch {
        navigate('/settings/clinic');
      }
    };
  } else {
    // trialing with > 4 days remaining — no banner needed
    return null;
  }

  return (
    <div className={`${bgClass} text-white text-center py-2 px-4 text-sm font-medium flex items-center justify-center gap-3 relative`}>
      <span>{message}</span>
      <button
        onClick={ctaAction}
        className="px-3 py-1 bg-white/20 rounded-md hover:bg-white/30 text-xs font-semibold transition-colors"
      >
        {ctaLabel}
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-white/20 rounded transition-colors"
        aria-label="Dismiss"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
